import React, { useEffect, useState } from "react";
import { Map, MapMarker, MarkerContent, MarkerTooltip, MapControls } from "@repo/ui/map";
import type { SectionComponentProps } from "../../registry.js";
import { geocodeAddress } from "../../lib/geocode.js";
import { logConversionEvent } from "@repo/backend/firebase/analytics";

interface MapEmbedProps {
  /** Street address — geocoded via Nominatim on mount if lon/lat not provided */
  address?: string;
  /** WGS-84 longitude — skips geocoding when provided alongside latitude */
  longitude?: number;
  /** WGS-84 latitude — skips geocoding when provided alongside longitude */
  latitude?: number;
  /** Initial zoom level (default: 15) */
  zoom?: number;
  /** Tooltip label on the map marker (falls back to address) */
  markerLabel?: string;
  headline?: string;
  phone?: string;
  email?: string;
}

interface Coords { lng: number; lat: number }

function ContactSidebar({ address, phone, email }: Pick<MapEmbedProps, "address" | "phone" | "email">) {
  if (!address && !phone && !email) return null;
  return (
    <div className="space-y-4 pt-2">
      {address && (
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-primary mb-1">Address</p>
          <p className="text-foreground whitespace-pre-line text-sm">{address}</p>
        </div>
      )}
      {phone && (
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-primary mb-1">Phone</p>
          <a href={`tel:${phone}`} onClick={() => logConversionEvent("click_call", { phone })} className="text-foreground hover:text-primary transition-colors text-sm">{phone}</a>
        </div>
      )}
      {email && (
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-primary mb-1">Email</p>
          <a href={`mailto:${email}`} className="text-foreground hover:text-primary transition-colors text-sm">{email}</a>
        </div>
      )}
    </div>
  );
}

function MapPlaceholder({ geocoding }: { geocoding: boolean }) {
  return (
    <div className="flex items-center justify-center h-full min-h-64 text-muted-foreground text-sm text-center p-8 bg-muted rounded-[var(--radius)] border border-border">
      {geocoding ? (
        <div>
          <div className="flex gap-1 justify-center mb-3">
            {[0, 150, 300].map((d) => (
              <span key={d} className="block w-2 h-2 rounded-full bg-muted-foreground/50 animate-pulse" style={{ animationDelay: `${d}ms` }} />
            ))}
          </div>
          <p className="text-xs">Locating address…</p>
        </div>
      ) : (
        <div>
          <div className="text-4xl mb-3">📍</div>
          <p className="font-medium mb-1">No location set</p>
          <p className="text-xs">
            Add <code className="bg-background px-1 py-0.5 rounded">address</code> to section props
          </p>
        </div>
      )}
    </div>
  );
}

export function MapEmbed({ props }: SectionComponentProps) {
  const {
    address,
    longitude,
    latitude,
    zoom = 15,
    markerLabel,
    headline,
    phone,
    email,
  } = props as MapEmbedProps;

  const hasExplicitCoords = longitude !== undefined && latitude !== undefined;

  const [coords, setCoords] = useState<Coords | null>(
    hasExplicitCoords ? { lng: longitude!, lat: latitude! } : null
  );
  const [geocoding, setGeocoding] = useState(false);
  const [geocodeFailed, setGeocodeFailed] = useState(false);

  useEffect(() => {
    if (hasExplicitCoords || !address || coords || geocodeFailed) return;

    let cancelled = false;
    setGeocoding(true);

    geocodeAddress(address).then((result) => {
      if (cancelled) return;
      setGeocoding(false);
      if (result) {
        setCoords({ lng: result.longitude, lat: result.latitude });
      } else {
        setGeocodeFailed(true);
      }
    });

    return () => { cancelled = true; };
  }, [address, hasExplicitCoords, coords, geocodeFailed]);

  const hasContact = !!(address || phone || email);
  const tooltipLabel = markerLabel ?? address;

  return (
    <section className="py-16 px-6 bg-muted/30" id="map">
      <div className="max-w-5xl mx-auto">
        {headline && (
          <h2 className="text-2xl font-bold text-foreground mb-8 text-center">{headline}</h2>
        )}
        <div className={hasContact ? "grid md:grid-cols-2 gap-8 items-start" : "w-full"}>
          <div className="rounded-[var(--radius)] overflow-hidden border border-border aspect-video">
            {coords ? (
              <Map center={[coords.lng, coords.lat]} zoom={zoom} interactive={true}>
                <MapMarker longitude={coords.lng} latitude={coords.lat}>
                  <MarkerContent>
                    <div className="w-4 h-4 rounded-full bg-primary border-2 border-white shadow-lg" />
                  </MarkerContent>
                  {tooltipLabel && <MarkerTooltip>{tooltipLabel}</MarkerTooltip>}
                </MapMarker>
                <MapControls showZoom position="bottom-right" />
              </Map>
            ) : (
              <MapPlaceholder geocoding={geocoding} />
            )}
          </div>

          {hasContact && (
            <ContactSidebar address={address} phone={phone} email={email} />
          )}
        </div>
      </div>
    </section>
  );
}
